/*
 * Copyright (c) 2024, ResMed Inc. All rights reserved.
 */
import { PlatformLocation } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injectable,
} from '@angular/core';
import { environment } from 'src/app/environment/environment';
import {
  SurveyResponses,
  Question,
  GroupedQuestions,
} from '../../interfaces/survey_response_interfaces';
import {
  copyToClipboard,
  getFormattedDate,
  buildQueryString,
  queryErrShow,
  switchboardCall,
} from 'src/app/utils/common_functions';

let selectedValue: string;
@Injectable({
  providedIn: 'root',
})
@Component({
  selector: 'app-survey_response',
  templateUrl: './survey_response.component.html',
  styleUrls: ['./survey_response.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SurveyResponseComponent implements AfterViewInit {
  isExpanded = false;
  selectedValue: string = 'airview-web';
  declineEnabled: boolean = false;

  surveyResponses: SurveyResponses = {
    surveyResponses: '',
    questions: [],
    groupedQuestions: [],
  };

  response: any = '';
  queryError: boolean = false;
  errorMsg: string = '';

  lastKey: string = '';
  lastDayOffset: number = -1;
  displaySearch: boolean = false;

  results: any;
  feedbackBlock: any;
  answerBlock: any;
  declineBlock: any;
  answered: any;
  popoverContent: any;

  constructor(
    private elementRef: ElementRef,
    private httpClient: HttpClient,
    private cdr: ChangeDetectorRef,
    private platformLocation: PlatformLocation
  ) {}

  ngOnInit() {}

  ngAfterViewInit(): void {}

  /* QUERY FUNCTIONS
     ===================================================================== */
  async search(): Promise<void> {
    //Get all table elements
    const table = this.elementRef.nativeElement.querySelector(
      '#resultTable'
    ) as HTMLTableElement;
    const results = this.elementRef.nativeElement.querySelector(
      '#results'
    ) as HTMLDivElement;
    const txtField = this.elementRef.nativeElement.querySelector(
      '#source-id'
    ) as HTMLInputElement;
    const searchBtn = this.elementRef.nativeElement.querySelector(
      '#searchBtn'
    ) as HTMLButtonElement;
    table.appendChild(document.createElement('tbody'));
    const tbody = document.querySelector(
      'table tbody'
    ) as HTMLTableSectionElement;

    if (!txtField.value) {
      results.classList.add('hidden');
      this.errorMsg = 'No query parameters were provided';
      //Force detect changes so updated error msg is loaded
      this.cdr.detectChanges();
      queryErrShow(this.elementRef);
      return;
    }

    const queryString = buildQueryString(txtField.value);

    //Wait for query to execute then format data
    console.log('Search fired off with values: ', txtField.value);
    await this.executeQuery(queryString);

    //Check if query returned responses
    if (
      !this.surveyResponses?.surveyResponses?.questions &&
      this.queryError == false
    ) {
      console.log('No values found');
      this.queryError = true;
      this.errorMsg = 'Query returned no results, check console logs';
    }

    if (this.queryError) {
      results.classList.add('hidden');
      //Force detect changes so updated error msg is loaded
      this.cdr.detectChanges();
      queryErrShow(this.elementRef);
      this.queryError = false;
      return;
    }

    this.splitByKey();

    //Set all display flags to true to display results and force detect changes to load in
    this.displaySearch = true;
    results.classList.remove('hidden');
    this.cdr.detectChanges();
  }

  async executeQuery(queryString: string): Promise<void> {
    let uri = environment.surveyApiUri;
    if (this.selectedValue !== '') {
      uri += '?clientId=' + this.selectedValue;
    }

    if (this.declineEnabled == true) {
      if (this.selectedValue == '') {
        uri += '?includeDeclines=true';
      } else {
        uri += '&includeDeclines=true';
      }
    }

    try {
      this.surveyResponses = await switchboardCall(
        { surveyResponses: [{ sourceId: queryString }] },
        uri,
        this.platformLocation,
        this.httpClient
      );
    } catch (error) {
      console.error(error);
      this.errorMsg =
        'An error occurred: ' + (error as Error).message + ' Check logs';
      this.queryError = true;
    }
  }

  save(event: Event): void {
    const selected = (event.target as HTMLSelectElement).value;
    this.selectedValue = selected;
  }

  //Expands all accordion elements on page
  toggleAccordion(): void {
    this.isExpanded = !this.isExpanded;
    const accordionItems = document.querySelectorAll(
      '.accordion-item'
    ) as NodeListOf<HTMLElement>;
    accordionItems.forEach((item) => {
      item
        .querySelector('.accordion-collapse')
        ?.classList.toggle('show', this.isExpanded);
      item
        .querySelector('.accordion-button')
        ?.setAttribute('aria-expanded', String(this.isExpanded));
    });
  }

  innerFormatDate(date: string): string {
    return date.replace('T', ' ').replace(/\+\d{2}:\d{2}$/, '');
  }

  splitByKey(): void {
    this.surveyResponses.groupedQuestions = [];

    let currentGroup: GroupedQuestions | null = null;

    this.surveyResponses.surveyResponses.questions.forEach(
      (question: Question) => {
        const formattedDate = getFormattedDate(question.dateTime);

        if (question.groupByKey !== this.lastKey) {
          currentGroup = {
            date: formattedDate,
            dayOffset: question.dayOffset,
            questions: [question],
          };
          this.surveyResponses.groupedQuestions.push(currentGroup);
          this.lastKey = question.groupByKey;
        } else {
          if (currentGroup) {
            currentGroup.questions.push(question);
          }
        }
      }
    );
    this.lastKey = '';
  }

  copyToClipboard(text: string | number): void {
    copyToClipboard(text);
  }
}
